import React, { useRef, useEffect } from 'react';
import { gsap } from './gsapClient';
import { useReducedMotion } from './useReducedMotion';

interface RevealProps {
    children: React.ReactNode;
    /** Stagger delay in seconds — pass index * step from the parent list */
    delay?: number;
    /** Vertical travel distance in px (default 24) */
    y?: number;
    /** ScrollTrigger start position (default 'top 88%') */
    start?: string;
    className?: string;
    style?: React.CSSProperties;
}

/**
 * Reveal — scroll-triggered entrance wrapper.
 *
 * Content renders visible by default; GSAP hides + animates it only when
 * JS runs and motion is allowed, so headless renderers and reduced-motion
 * users always see the content.
 *
 * Usage:
 *   {items.map((item, i) => (
 *     <Reveal key={item.id} delay={i * 0.06}><Card {...item} /></Reveal>
 *   ))}
 */
export const Reveal: React.FC<RevealProps> = ({
    children,
    delay = 0,
    y = 24,
    start = 'top 88%',
    className,
    style,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (reduced) {
            gsap.set(el, { opacity: 1, y: 0 });
            return;
        }
        // `immediateRender: false` is what makes the docstring above true.
        // Without it GSAP applies `opacity: 0` on creation, and any environment
        // where ScrollTrigger never fires — a headless renderer, a failed
        // plugin load, a browser that reports a zero-height viewport — leaves
        // the content permanently invisible. With it, the element stays visible
        // and only animates once the trigger actually resolves.
        const tween = gsap.fromTo(
            el,
            { opacity: 0, y },
            {
                opacity: 1,
                y: 0,
                duration: 0.75,
                delay,
                // expo.out over power3.out: a longer, softer tail so entrances
                // read as settling rather than stopping — the "fluid" note.
                ease: 'expo.out',
                immediateRender: false,
                scrollTrigger: { trigger: el, start, once: true },
            }
        );
        return () => {
            tween.scrollTrigger?.kill();
            tween.kill();
        };
    }, [delay, y, start, reduced]);

    return (
        <div ref={ref} className={className} style={style}>
            {children}
        </div>
    );
};
